import { NsfItemReturn } from '../clients/rust-api-client';

/** How the bank's cutoff reads to a person: in Central, locally, and how long is left. */
export interface CutoffDescription {
  /** e.g. "Sep 29, 1:00 PM CT" — the bank's rule, in the bank's zone. */
  central: string;
  /** The same instant in the browser's zone, or null when it is Central. */
  local: string | null;
  /** "3 hours left", "passed", … */
  remaining: string;
  passed: boolean;
}

const CENTRAL = 'America/Chicago';

/**
 * Describe the decision deadline the API computed (`decision_cutoff_at`,
 * 1pm Central on the expiry date, as a UTC instant). The bank's rule is
 * stated in Central, so that rendering leads; the local one follows for
 * anyone not in Central.
 */
export const describeCutoff = (
  iso?: string | null,
  now: Date = new Date()
): CutoffDescription | null => {
  if (!iso) return null;
  const at = new Date(iso);
  if (isNaN(at.getTime())) return null;

  const central = `${at.toLocaleString('en-US', {
    timeZone: CENTRAL,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} CT`;

  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const local =
    browserZone === CENTRAL
      ? null
      : at.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });

  const ms = at.getTime() - now.getTime();
  const passed = ms <= 0;
  let remaining: string;
  if (passed) {
    remaining = 'passed';
  } else if (ms < 60 * 60_000) {
    const m = Math.max(1, Math.round(ms / 60_000));
    remaining = `${m} ${m === 1 ? 'minute' : 'minutes'} left`;
  } else if (ms < 48 * 60 * 60_000) {
    const h = Math.round(ms / 3_600_000);
    remaining = `${h} ${h === 1 ? 'hour' : 'hours'} left`;
  } else {
    const d = Math.round(ms / 86_400_000);
    remaining = `${d} days left`;
  }
  return { central, local, remaining, passed };
};

/** "check #1130" or "ACH debit from Company". */
export const nsfItemLabel = (item: NsfItemReturn): string => {
  if (item.source === 'ACH') {
    const who = item.ach_company_name || item.ach_individual_name;
    return who ? `ACH debit from ${who}` : 'ACH debit';
  }
  return `check #${item.check_number ?? '?'}`;
};
